import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom scroll restoration component that handles page navigation
 * while preserving scroll position during pagination
 */
const ScrollRestoration = () => {
  const { pathname, search } = useLocation();
  const lastPathRef = useRef(pathname);
  const lastSearchRef = useRef('');
  const scrollPositionsRef = useRef({});
  const isPaginationChangeRef = useRef(false);
  
  // Detect changes to URL search parameters to identify pagination changes
  useEffect(() => {
    // Extract page parameter to detect pagination
    const currentParams = new URLSearchParams(search);
    const previousParams = new URLSearchParams(lastSearchRef.current);
    
    const currentPage = currentParams.get('page');
    const previousPage = previousParams.get('page');
    
    // If only the page parameter changed, mark this as a pagination change
    if (pathname === lastPathRef.current && currentPage !== previousPage) {
      isPaginationChangeRef.current = true;
    } else {
      isPaginationChangeRef.current = false;
    }
    
    lastSearchRef.current = search;
  }, [pathname, search]);
  
  // Handle scroll restoration on navigation
  useEffect(() => {
    // Skip scroll restoration if this is a pagination change (infinite scroll)
    if (isPaginationChangeRef.current) {
      console.log('Pagination change detected - preserving scroll position');
      return;
    }
    
    // For actual route changes (not pagination), restore saved positions or scroll to top
    if (pathname !== lastPathRef.current) {
      // Save scroll position for the path we're leaving
      scrollPositionsRef.current[lastPathRef.current] = window.scrollY;
      
      // Set timeout to allow DOM to update before scrolling
      setTimeout(() => {
        // Restore previous position if available, otherwise scroll to top
        if (scrollPositionsRef.current[pathname]) {
          window.scrollTo(0, scrollPositionsRef.current[pathname]);
        } else {
          window.scrollTo(0, 0);
        }
      }, 0);
      
      lastPathRef.current = pathname;
    }
  }, [pathname]);
  
  return null; // This is a behavior-only component with no UI
};

export default ScrollRestoration;
